// frontend/src/services/api.js
// WHY CHANGED:
//   - serviceAPI.getServices renamed to getByCoupon and changed from two
//     positional arguments (couponNo, mobile) to one object argument
//     ({ couponNo, mobile }). CustomerDetail.jsx was already calling
//     serviceAPI.getByCoupon({...}) — that method didn't exist under that
//     name/shape, so the call threw a TypeError before any request was
//     sent. This is what was actually causing "Failed to load services"
//     and services staying stuck on "Available".
//   - couponAPI.getAudit changed from a single membershipNo string
//     concatenated into the URL path, to an object sent as query params.
//     The old version produced literal "[object Object]" in the request
//     URL whenever an object was passed in instead of a string, which is
//     exactly what CustomerDetail.jsx was doing.

import axios from 'axios';
const API_BASE = process.env.REACT_APP_API_URL || '/api';
const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
});

// Attach JWT to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 globally
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/login', credentials),
};

export const customerAPI = {
  search:  (q)  => api.get('/customers/search', { params: { q } }),
  getById: (id) => api.get(`/customers/${id}`),
  updateMobile: (payload) =>
    api.put('/customers/update-mobile', payload),
};

export const serviceAPI = {
  // Matches CustomerDetail.jsx's loadServices(): serviceAPI.getByCoupon({ couponNo, mobile })
  getByCoupon: ({ couponNo, mobile }) =>
    api.get('/customer/services', {
      params: { couponNo, mobile },
    }),
  claimServices: (payload) => api.post('/services/claim', payload),
};

export const couponAPI = {
  cancel:     (payload) => api.post('/coupons/cancel',     payload),
  inactive:   (payload) => api.post('/coupons/inactive',   payload),
  reactivate: (payload) => api.post('/coupons/reactivate', payload),
  // Matches CustomerDetail.jsx's loadAuditLog(): couponAPI.getAudit({ couponNo, mobile })
  // NOTE: this also requires the route in routes/index.js to accept query
  // params instead of a :membershipNo path param (fixed separately), AND
  // requires couponController.js's getAuditLog to read req.query.couponNo /
  // req.query.mobile and match against coupon_no — that file hasn't been
  // provided, so this half of the fix is unverified.
  getAudit: ({ couponNo, mobile }) =>
    api.get('/coupons/audit', {
      params: { couponNo, mobile },
    }),
};

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

export const uploadAPI = {
  uploadExcel: (file, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
  },
};

export default api;